import { ThemeName, ThemeObject, themes } from 'types/theme';
import { persist } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';

enum LiveDisplayQRCodeValue {
  ScanToDonate = 'scan_to_donate',
  PaddleRaise = 'paddle_raise',
}

type LiveDisplayQRCodeType = LiveDisplayQRCodeValue | null;

interface LiveDisplaySettings {
  theme: ThemeObject;
  showQRCode?: boolean;
  qrCodeType?: LiveDisplayQRCodeType;
  showSMSKeyword: boolean;
  showAmountRaised: boolean;
  showShoutouts: boolean;
  campaignId: string;
}

interface LiveDisplayState {
  campaigns: {
    [campaignId: string]: { liveDisplaySettings: LiveDisplaySettings };
  };
  hasDismissedSmallScreenWarning: boolean;
  setHasDismissedSmallScreenWarning: (hasDismissedSmallScreenWarning: boolean) => void;
  setLiveDisplaySettings: (liveDisplaySettings: LiveDisplaySettings) => void;
}

const getDefaultLiveDisplaySettings = (campaignId: string): LiveDisplaySettings => ({
  campaignId,
  showAmountRaised: true,
  showQRCode: true,
  showSMSKeyword: true,
  showShoutouts: true,
  theme: themes[ThemeName.aurora],
});

const getLiveDisplayQRCodeType = (
  liveDisplaySettings: Pick<LiveDisplaySettings, 'showQRCode' | 'qrCodeType'>,
) => {
  if (liveDisplaySettings.showQRCode === false) {
    return null;
  }

  if (liveDisplaySettings.qrCodeType === LiveDisplayQRCodeValue.PaddleRaise) {
    return LiveDisplayQRCodeValue.PaddleRaise;
  }

  if (
    liveDisplaySettings.showQRCode ||
    liveDisplaySettings.qrCodeType === LiveDisplayQRCodeValue.ScanToDonate
  ) {
    return LiveDisplayQRCodeValue.ScanToDonate;
  }

  return null;
};

const useStore = create<LiveDisplayState>()(
  persist(
    (set, get) => ({
      campaigns: {
        default: {
          liveDisplaySettings: getDefaultLiveDisplaySettings('default'),
        },
      },
      hasDismissedSmallScreenWarning: false,
      setLiveDisplaySettings: (liveDisplaySettings: LiveDisplaySettings) => {
        set({
          campaigns: {
            ...get().campaigns,
            [liveDisplaySettings.campaignId]: {
              liveDisplaySettings: {
                ...liveDisplaySettings,
              },
            },
          },
        });
      },

      setHasDismissedSmallScreenWarning: (hasDismissedSmallScreenWarning: boolean) =>
        set({ hasDismissedSmallScreenWarning }),
    }),
    {
      name: 'live-display-settings',
    },
  ),
);

export { getDefaultLiveDisplaySettings };
export { getLiveDisplayQRCodeType };
export { LiveDisplayQRCodeValue };
export type { LiveDisplaySettings, LiveDisplayQRCodeType };
export { useStore };
